import axios from 'axios';
import { useEffect, useRef, useState } from 'react';

const NOMINATIM_SEARCH_URL = 'https://nominatim.openstreetmap.org/search';
const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_DELAY_MS = 500;

/**
 * Convertit un résultat Nominatim (addressdetails=1) en champs d'adresse du destinataire
 * @param {object} result - Résultat de recherche Nominatim
 * @returns {object} - { street, zipcode, city, country, latitude, longitude }
 */
export const toAddressFields = (result) => {
  const details = result?.address || {};
  const road =
    details.road ||
    details.pedestrian ||
    details.footway ||
    details.square ||
    details.place;
  const street =
    [details.house_number, road].filter(Boolean).join(' ') ||
    result?.display_name?.split(', ')?.[0] ||
    '';

  return {
    street,
    zipcode: details.postcode || '',
    city:
      details.city ||
      details.town ||
      details.village ||
      details.municipality ||
      details.hamlet ||
      '',
    country: (details.country_code || 'fr').toUpperCase(),
    latitude: parseFloat(result.lat),
    longitude: parseFloat(result.lon),
  };
};

/**
 * Suggestions d'adresses (Nominatim / OpenStreetMap, France) pendant la saisie, avec anti-rebond
 * @param {string} query - Texte saisi
 * @returns {{ suggestions: object[], isSearching: boolean, clearSuggestions: Function }}
 */
export default function useAddressSuggestions(query) {
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const timeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const trimmedQuery = query?.trim() || '';
    if (trimmedQuery.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setIsSearching(false);
      return undefined;
    }

    setIsSearching(true);
    timeoutRef.current = setTimeout(async () => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      try {
        const response = await axios.get(NOMINATIM_SEARCH_URL, {
          params: {
            format: 'json',
            addressdetails: 1,
            countrycodes: 'fr',
            limit: 5,
            q: trimmedQuery,
          },
          headers: { 'Accept-Language': 'fr' },
          signal: abortControllerRef.current.signal,
        });
        setSuggestions(Array.isArray(response.data) ? response.data : []);
        setIsSearching(false);
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error('Erreur lors de la recherche géographique:', error);
          setSuggestions([]);
          setIsSearching(false);
        }
      }
    }, DEBOUNCE_DELAY_MS);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [query]);

  useEffect(() => () => abortControllerRef.current?.abort(), []);

  const clearSuggestions = () => {
    abortControllerRef.current?.abort();
    setSuggestions([]);
    setIsSearching(false);
  };

  return { suggestions, isSearching, clearSuggestions };
}
